import { X } from "lucide-react";
import type { TabType } from "../App";

// Define which categories should be paired together (matching CategoriesTab)
const categoryPairs = [
  { display: "Arts & Culture", categories: ["Arts", "Culture"] },
  { display: "Food & Drinks", categories: ["Food", "Drinks"] },
  { display: "Nature & Outdoor", categories: ["Nature", "Outdoor"] },
  { display: "Health & Support", categories: ["Health", "Support"] }
];

interface FilterBadgesProps {
  activeTab: TabType;
  badgeContainerRef: React.RefObject<HTMLDivElement>;
  hasNoFiltersActive: () => boolean;
  hasNoLocationFiltersActive: () => boolean;
  selectedContinents: string[];
  selectedCountries: string[];
  selectedCities: string[];
  selectedWorldMaps: boolean;
  selectedCategories: string[];
  selectedSearchTags: string[];
  filteredLinksCount: number;
  clearLocationFilters: () => void;
  removeContinentBadge: (continent: string) => void;
  removeCountryBadge: (country: string) => void;
  removeCityBadge: (city: string) => void;
  removeWorldMapsBadge: () => void;
  clearCategoriesFromBadge: () => void;
  clearTagsFromBadge: () => void;
  animateBadge: boolean;
}

export function FilterBadges({
  activeTab,
  badgeContainerRef,
  hasNoFiltersActive,
  hasNoLocationFiltersActive,
  selectedContinents,
  selectedCountries,
  selectedCities,
  selectedWorldMaps,
  selectedCategories,
  selectedSearchTags,
  filteredLinksCount,
  clearLocationFilters,
  removeContinentBadge,
  removeCountryBadge,
  removeCityBadge,
  removeWorldMapsBadge,
  clearCategoriesFromBadge,
  clearTagsFromBadge,
  animateBadge,
}: FilterBadgesProps) {
  // Render invisibly when on About tab (for measurement), visible otherwise
  const isAboutTab = activeTab === "about";

  return (
    <div 
      ref={badgeContainerRef} 
      className={`fixed bottom-15 md:bottom-16 left-0 right-0 flex items-center justify-center z-40 pointer-events-none px-4 ${isAboutTab ? 'no-transitions' : ''}`}
      style={{
        visibility: isAboutTab ? 'hidden' : 'visible',
        pointerEvents: isAboutTab ? 'none' : 'auto'
      }}
    >
      <div className="w-full max-w-[1600px] flex items-center justify-center">
      <div className="pointer-events-auto" role="region" aria-label="Active filters">
        {hasNoFiltersActive() ? (
          <div 
            className="inline-flex items-baseline gap-1 border border-transparent pl-6 pr-3 py-1.5 rounded-[8px]"
          >
            <span className="font-bold text-white text-sm">
              All Maps
            </span>
            <span className="text-xs text-[#00ff00] px-1 rounded-full">
              {filteredLinksCount}
            </span>
          </div>
        ) : (
        <div className="flex flex-row flex-wrap items-center justify-center gap-2 transition-[height] duration-100 ease-in-out">
            {/* Render badges sorted by text length (shortest to longest) */}
            {(() => {
              const badges: Array<{ text: string; length: number; jsx: JSX.Element; key: string }> = [];

              if (!hasNoLocationFiltersActive()) {
                const filters = [];
                // Show only the lowest active location level
                if (selectedCities.length > 0) {
                  filters.push(selectedCities.join(", "));
                } else if (selectedCountries.length > 0) {
                  filters.push(selectedCountries.join(", "));
                } else if (selectedContinents.length > 0) {
                  filters.push(selectedContinents.join(", "));
                } else if (selectedWorldMaps) {
                  filters.push("World Maps");
                }
                const locationText = filters.join(" • ");
                
                badges.push({
                  key: 'location',
                  text: locationText,
                  length: locationText.length,
                  jsx: (
                    <button
                      key="location"
                      onClick={() => {
                        if (activeTab === "location") {
                          if (selectedCities.length > 0) {
                            removeCityBadge(selectedCities[0]);
                          } else if (selectedCountries.length > 0) {
                            removeCountryBadge(selectedCountries[0]);
                          } else if (selectedContinents.length > 0) {
                            removeContinentBadge(selectedContinents[0]);
                          } else if (selectedWorldMaps) {
                            removeWorldMapsBadge();
                          }
                        } else {
                          clearLocationFilters();
                        }
                      }}
                      className="inline-flex items-center gap-1 bg-[var(--accent-blue)]/10 border border-[var(--accent-blue)] pl-6 pr-3 py-1.5 shadow-[0_4px_10px_rgba(0,0,0,0.2)] rounded-[8px] transition-all cursor-pointer"
                      aria-label={activeTab === "location" ? "Go back one location level" : "Clear location filters"}
                    >
                      <span className="font-bold text-white text-sm">
                        {locationText}
                      </span>
                      {/* Show counter only when no categories AND no tags */}
                      {selectedCategories.length === 0 && selectedSearchTags.length === 0 && (
                        <span 
                          className={`text-xs text-[#00ff00] px-1 rounded-full ml-1 ${animateBadge ? "animate-badge-pulse" : ""}`}
                          aria-live="polite"
                          aria-atomic="true"
                        >
                          {filteredLinksCount}
                        </span>
                      )}
                      <X className="w-3 h-3 text-white/85 ml-1" strokeWidth={2} />
                    </button>
                  )
                });
              }

              if (selectedCategories.length > 0) {
                const getCategoryDisplayText = () => {
                  let effectiveCount = 0;
                  let singleDisplayName = "";
                  const accountedCategories = new Set<string>();
                  
                  for (const pair of categoryPairs) {
                    const hasAnyPairCategories = pair.categories.some(cat => 
                      selectedCategories.includes(cat)
                    );
                    
                    if (hasAnyPairCategories) {
                      effectiveCount++;
                      singleDisplayName = pair.display;
                      pair.categories.forEach(cat => accountedCategories.add(cat));
                    }
                  }
                  
                  for (const cat of selectedCategories) {
                    if (!accountedCategories.has(cat)) {
                      effectiveCount++;
                      singleDisplayName = cat;
                    }
                  }
                  
                  if (effectiveCount === 1) {
                    return singleDisplayName;
                  } else {
                    return `${effectiveCount} Categories`;
                  }
                };
                
                const categoryText = getCategoryDisplayText();
                
                badges.push({
                  key: 'category',
                  text: categoryText,
                  length: categoryText.length,
                  jsx: (
                    <button
                      key="category"
                      onClick={clearCategoriesFromBadge}
                      className="inline-flex items-center gap-1 bg-[var(--accent-blue)]/10 border border-[var(--accent-blue)] pl-6 pr-3 py-1.5 shadow-[0_4px_10px_rgba(0,0,0,0.2)] rounded-[8px] transition-all cursor-pointer"
                      aria-label="Clear category filters"
                    >
                      <span className="font-bold text-white text-sm">
                        {categoryText}
                      </span>
                      {/* Show counter only when no tags */}
                      {selectedSearchTags.length === 0 && (
                        <span className="text-xs text-[#00ff00] px-1 rounded-full ml-1">
                          {filteredLinksCount}
                        </span>
                      )}
                      <X className="w-3 h-3 text-white/85 ml-1" strokeWidth={2} />
                    </button>
                  )
                });
              }

              if (selectedSearchTags.length > 0) {
                const tagsText = selectedSearchTags.join(", ");
                
                badges.push({
                  key: 'tags',
                  text: tagsText,
                  length: tagsText.length,
                  jsx: (
                    <button
                      key="tags"
                      onClick={clearTagsFromBadge}
                      className="inline-flex items-center gap-1 bg-[var(--accent-blue)]/10 border border-[var(--accent-blue)] pl-6 pr-3 py-1.5 shadow-[0_4px_10px_rgba(0,0,0,0.2)] rounded-[8px] transition-all cursor-pointer"
                      aria-label="Clear search tags"
                    >
                      <span className="font-bold text-white text-sm capitalize">
                        {tagsText}
                      </span>
                      {/* Always show counter - highest priority */}
                      <span className="text-xs text-[#00ff00] px-1 rounded-full ml-1">
                        {filteredLinksCount}
                      </span>
                      <X className="w-3 h-3 text-white/85 ml-1" strokeWidth={2} />
                    </button>
                  )
                });
              }

              badges.sort((a, b) => a.length - b.length);

              return badges.map(badge => badge.jsx);
            })()}
        </div>
        )}
      </div>
      </div>
    </div>
  );
}